import { Autocomplete } from '@Components/Autocomplete/Autocomplete';
import EdNumericInput from '@Components/UI/Inputs/EdNumericInput/EdNumericInput';
import {
	fetchBundlesDropdown,
	fetchCoursesDropdown,
	fetchStandaloneCoursesDropdown,
} from '@Features/dropdowns/dropdownsSlice';
import { productOptionLabel } from '@Features/dropdowns/dropdownsSlice.types';
import { useTypedSelector } from '@Features/store';
import usePlatformMode from '@Hooks/usePlatformMode';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { Spacer } from '@Styled/utilities';
import React, { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import * as yup from 'yup';

/**
 * The one target a scratch card is bound to, shared by the Generate, Edit and
 * Bulk Edit modals so the mode list, the field set, the validation and the
 * payload mapping cannot drift between them.
 *
 * `Instructor` is unconditional: an instructor-wide card works on classroom
 * courses, standalone courses and bundles alike, so it is the one option valid
 * in every platform mode. Everything else is gated by the corresponding
 * setting.
 */
export type GenerationMode =
	| 'Generic'
	| 'Specific'
	| 'Instructor'
	| 'Standalone'
	| 'Bundle';

export const MODE_LABELS: Record<GenerationMode, string> = {
	Generic: 'Generic',
	Specific: 'Specific',
	Instructor: 'Instructor',
	Standalone: 'Standalone Course',
	Bundle: 'Bundle',
};

// A cleared TextField holds '' (type a value, delete it) and a cleared
// Autocomplete writes '' into its DOM input. yup's number cast turns '' into
// NaN and fails the whole schema — which left Submit silently dead after
// clearing the optional instructor value. Map '' back to null before
// validation runs.
export const emptyToNull = (value: unknown, originalValue: unknown) =>
	originalValue === '' ? null : value;

/**
 * The target half of a modal's schema. Compose with the modal's own fields:
 * `yup.object().shape({ ...TARGET_SCHEMA_FIELDS, quantity: ..., checker: ... })`.
 * Every field keys off `mode`, so the composed schema must keep a `mode` field.
 */
export const TARGET_SCHEMA_FIELDS = {
	mode: yup.string().notRequired(),
	course_id: yup
		.number()
		.label('Course')
		.transform(emptyToNull)
		.nullable()
		.when('mode', {
			is: (value: string) => value === 'Specific',
			then: (s) => s.required(),
			otherwise: (s) => s.nullable(true),
		}),
	standalone_course_id: yup
		.number()
		.label('Standalone course')
		.transform(emptyToNull)
		.nullable()
		.when('mode', {
			is: (value: string) => value === 'Standalone',
			then: (schema) => schema.required(),
			otherwise: (schema) => schema.nullable(true),
		}),
	bundle_id: yup
		.number()
		.label('Bundle')
		.transform(emptyToNull)
		.nullable()
		.when('mode', {
			is: (value: string) => value === 'Bundle',
			then: (schema) => schema.required(),
			otherwise: (schema) => schema.nullable(true),
		}),
	instructor_id: yup
		.number()
		.label('Instructor')
		.transform(emptyToNull)
		.nullable(true)
		.when('mode', {
			is: (val: string) => val === 'Instructor',
			then: (schema) => schema.required(),
			otherwise: (schema) => schema.nullable(true),
		}),
	classroom_id: yup
		.number()
		.label('Classroom')
		.transform(emptyToNull)
		.nullable(true)
		.when('mode', {
			// Only the two classroom modes need one. Bundle used to fall through
			// to `otherwise` and was wrongly required.
			is: (val: string) => val === 'Generic' || val === 'Specific',
			then: (schema) => schema.required(),
			otherwise: (schema) => schema.nullable(true),
		}),
	// Optional on purpose: an instructor card with no value is a wildcard usable
	// on any of that instructor's products, at any price. A value of 0 is
	// deliberately treated the same — do not reject it. min(0) only fences off
	// pasted negatives (the keypress filter already blocks typing them).
	value: yup
		.number()
		.label('Value')
		.transform(emptyToNull)
		.typeError('Value must be a number')
		.min(0)
		.nullable(true),
};

/**
 * Only the target the chosen mode owns reaches the payload. The backend
 * rejects a payload carrying more than one, so a stale value left over from a
 * previous mode would be a hard error rather than a silent mis-target.
 */
export const buildTargetPayload = (
	mode: GenerationMode,
	formData: Record<string, any>
): Record<string, any> => {
	const payload: Record<string, any> = {};
	if (mode === 'Generic') payload.classroom_id = formData.classroom_id;
	if (mode === 'Specific') {
		payload.classroom_id = formData.classroom_id;
		payload.course_id = formData.course_id;
	}
	if (mode === 'Instructor') {
		payload.instructor_id = formData.instructor_id;
		payload.value = formData.value || undefined;
	}
	if (mode === 'Standalone')
		payload.standalone_course_id = formData.standalone_course_id;
	if (mode === 'Bundle') payload.bundle_id = formData.bundle_id;
	return payload;
};

/**
 * The mode an existing (unused) card was generated in, for prefill. Product
 * first — product cards are stamped with their owner's instructor_id too, so
 * the instructor check must come last.
 */
export const modeForCard = (card: {
	bundle_id?: number | null;
	standalone_course_id?: number | null;
	course_id?: number | null;
	classroom_id?: number | null;
	instructor_id?: number | null;
}): GenerationMode => {
	if (card.bundle_id) return 'Bundle';
	if (card.standalone_course_id) return 'Standalone';
	if (card.course_id) return 'Specific';
	if (card.classroom_id) return 'Generic';
	return 'Instructor';
};

// The shared Autocomplete pins its inner TextField at 16.25rem, which left the
// dropdowns ragged against the modals' full-width inputs. textFieldProps keeps
// the override modal-local; module scope keeps the reference render-stable.
const FULL_WIDTH_FIELD = { sx: { width: '100%' } };

type Props = {
	control: any;
	setValue: (name: string, value: any) => void;
	watch: (name: string) => any;
	classRooms: any;
	/** The owning modal's open flag — gates the fetch/default-mode effects. */
	active: boolean;
};

export const TargetModePicker: React.FC<Props> = ({
	control,
	setValue,
	watch,
	classRooms,
	active,
}) => {
	const dispatch = useDispatch();
	const {
		dropdownInstructors,
		dropdownCourses,
		dropdownStandaloneCourses,
		dropdownBundles,
	} = useTypedSelector((state) => state.dropdowns);
	const { classroomsEnabled, standaloneEnabled, bundlesEnabled } =
		usePlatformMode();

	// Only the modes this platform mode actually supports. Instructor is always
	// available — those cards work on every product type.
	const availableModes = useMemo(() => {
		const modes: GenerationMode[] = [];
		if (classroomsEnabled) modes.push('Generic', 'Specific');
		modes.push('Instructor');
		if (standaloneEnabled) modes.push('Standalone');
		if (bundlesEnabled) modes.push('Bundle');
		return modes;
	}, [classroomsEnabled, standaloneEnabled, bundlesEnabled]);

	const mode: GenerationMode = watch('mode');
	const classroomId = watch('classroom_id');

	useEffect(() => {
		if (!active) return;
		if (standaloneEnabled) dispatch(fetchStandaloneCoursesDropdown(undefined));
		if (bundlesEnabled) dispatch(fetchBundlesDropdown(undefined));
	}, [active, standaloneEnabled, bundlesEnabled, dispatch]);

	// Default to the first mode this platform actually offers. It used to be
	// hardcoded to 'Generic', which is a dead option when classrooms are off.
	useEffect(() => {
		if (!active) return;
		if (!availableModes.includes(mode)) setValue('mode', availableModes[0]);
	}, [active, availableModes, mode, setValue]);

	useEffect(() => {
		if (classroomId) dispatch(fetchCoursesDropdown(classroomId));
	}, [classroomId, dispatch]);

	const withInstructor = mode === 'Instructor';
	const withStandaloneCourse = mode === 'Standalone';
	const withBundle = mode === 'Bundle';
	const withClassroom = mode === 'Generic' || mode === 'Specific';
	const withCourse = mode === 'Specific';

	return (
		<>
			<Controller
				name="mode"
				control={control}
				defaultValue={availableModes[0]}
				render={({ field: { onChange, ...field } }) => (
					<RadioGroupStyled
						aria-labelledby="radio-buttons-group-label"
						{...field}
						onChange={(e, v) => {
							// Clear every target when the mode changes: the backend
							// rejects a payload carrying more than one.
							setValue('classroom_id', null);
							setValue('course_id', null);
							setValue('standalone_course_id', null);
							setValue('bundle_id', null);
							setValue('instructor_id', null);
							setValue('value', null);
							onChange(e);
						}}
					>
						{availableModes.map((availableMode) => (
							<FormControlLabel
								key={availableMode}
								value={availableMode}
								control={<Radio />}
								label={MODE_LABELS[availableMode]}
							/>
						))}
					</RadioGroupStyled>
				)}
			/>

			{withClassroom && (
				<>
					<Controller
						name="classroom_id"
						control={control}
						render={({ field, fieldState }) => (
							<FormControl
								sx={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
								size="small"
							>
								<Autocomplete
									label="Select Classroom"
									options={classRooms.map((_: any) => ({
										id: _.id,
										// Same "Title (label) - Instructor" shape as the
										// standalone-course and bundle dropdowns.
										label: productOptionLabel(_),
									}))}
									size="small"
									{...field}
									fieldState={fieldState}
									textFieldProps={FULL_WIDTH_FIELD}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										// A course belongs to exactly one classroom. Keeping
										// the old pick across a classroom switch sent a
										// mismatched pair the backend rejects with a bare
										// "Row not found".
										setValue('course_id', null);
										field.onChange(e);
									}}
								/>
							</FormControl>
						)}
					/>
					{withCourse && (
						<Controller
							name="course_id"
							control={control}
							render={({ field, fieldState }) => (
								<FormControl
									fullWidth
									sx={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
									size="small"
								>
									<Autocomplete
										label="Select Course"
										options={dropdownCourses.map((_) => ({
											id: _.id,
											label: _.name,
										}))}
										size="small"
										{...field}
										fieldState={fieldState}
										textFieldProps={FULL_WIDTH_FIELD}
									/>
								</FormControl>
							)}
						/>
					)}
				</>
			)}
			{withStandaloneCourse && (
				<Controller
					name="standalone_course_id"
					control={control}
					render={({ field, fieldState }) => (
						<FormControl
							sx={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
							size="small"
						>
							<Autocomplete
								label="Select Standalone Course"
								options={dropdownStandaloneCourses.map((course) => ({
									id: course.id,
									label: productOptionLabel(course),
								}))}
								size="small"
								{...field}
								fieldState={fieldState}
								textFieldProps={FULL_WIDTH_FIELD}
							/>
						</FormControl>
					)}
				/>
			)}
			{withBundle && (
				<Controller
					name="bundle_id"
					control={control}
					render={({ field, fieldState }) => (
						<FormControl
							sx={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
							size="small"
						>
							<Autocomplete
								label="Select Bundle"
								options={dropdownBundles.map((bundle) => ({
									id: bundle.id,
									label: productOptionLabel(bundle),
								}))}
								size="small"
								{...field}
								fieldState={fieldState}
								textFieldProps={FULL_WIDTH_FIELD}
							/>
						</FormControl>
					)}
				/>
			)}

			{withInstructor && (
				<>
					<Controller
						control={control}
						name="instructor_id"
						render={({ field, fieldState }) => (
							<FormControl
								sx={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
								size="small"
							>
								<Autocomplete
									label="Select Instructor"
									options={dropdownInstructors.map((_) => ({
										id: _.id,
										label: `${_.first_name} ${_.last_name}`,
									}))}
									size="small"
									{...field}
									fieldState={fieldState}
									textFieldProps={FULL_WIDTH_FIELD}
								/>
							</FormControl>
						)}
					/>
					<Controller
						control={control}
						name="value"
						render={({ field, fieldState }) => (
							<Spacer my="0.5rem">
								<EdNumericInput
									fullWidth
									size="small"
									label="Set Value (optional)"
									placeholder="Value"
									error={fieldState.invalid}
									helperText={
										fieldState.error?.message ??
										"Leave empty (or 0) for a wildcard card, usable on any of this instructor's courses or bundles at any price."
									}
									{...field}
									variant="outlined"
								/>
							</Spacer>
						)}
					/>
				</>
			)}
		</>
	);
};

export default TargetModePicker;

const RadioGroupStyled = styled(RadioGroup)`
	display: flex;
	flex-direction: row;
	.MuiTypography-root {
		font-family: Roboto;
		font-size: 0.875rem;
		color: rgba(0, 0, 0, 0.87);
	}
	.MuiRadio-root.Mui-checked {
		color: #6c63ff;
	}
`;
